import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrencyDefinitionRecord, FinanceApiService, FxRateSnapshotRecord } from './finance-api.service';

type CurrencyDetailTab = 'general' | 'rates' | 'history';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-currency-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, InputNumberModule, TableModule, AinDatePipe],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.multiCurrency.currencies.createTitle') : t('finance.multiCurrency.currencies.detailTitle')" [description]="t('finance.multiCurrency.currencies.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ form.code || t('finance.multiCurrency.currencies.newRecord') }}</h3>
            <p>{{ form.name || t('finance.multiCurrency.currencies.newRecordHint') }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/financial/control-center/currencies']" [queryParams]="listQueryParams()" class="back-link">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode()) {
              <button pButton type="button" severity="secondary" [label]="t('finance.multiCurrency.actions.newFxRate')" (click)="createRateFromCurrency()"></button>
            }
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          <button type="button" [class.active]="activeTab() === 'rates'" [disabled]="isCreateMode()" (click)="activeTab.set('rates')">{{ t('finance.multiCurrency.tabs.rates') }}</button>
          <button type="button" [class.active]="activeTab() === 'history'" [disabled]="isCreateMode()" (click)="activeTab.set('history')">{{ t('finance.multiCurrency.tabs.history') }}</button>
        </section>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('columns.code') }}</span>
              <input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('columns.title') }}</span>
              <input pInputText [(ngModel)]="form.name" />
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.currencies.symbol') }}</span>
              <input pInputText [(ngModel)]="form.symbol" />
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.currencies.decimalPlaces') }}</span>
              <p-inputNumber [(ngModel)]="form.decimalPlaces" [useGrouping]="false" [min]="0" [max]="6" />
            </label>
            <div class="radio-wrapper">
              <span>{{ t('columns.status') }}</span>
              <div class="radio-group">
                <label class="radio-item">
                  <input type="radio" name="currency-active" [checked]="form.isActive" (change)="form.isActive = true" />
                  <span>{{ t('common.yes') }}</span>
                </label>
                <label class="radio-item">
                  <input type="radio" name="currency-active" [checked]="!form.isActive" (change)="form.isActive = false" />
                  <span>{{ t('common.no') }}</span>
                </label>
              </div>
            </div>
          </section>
        }

        @if (activeTab() === 'rates') {
          @if (!relatedRates().length) {
            <ain-state type="empty" [title]="t('common.noRecords')" />
          } @else {
            <p-table [value]="relatedRates()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.fromCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.toCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.fxRates.rate') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.effectiveDateUtc | ainDate }}</td>
                  <td>{{ row.fromCurrencyCode }}</td>
                  <td>{{ row.toCurrencyCode }}</td>
                  <td>{{ row.rate }}</td>
                  <td>
                    <button type="button" class="link-btn" (click)="openRate(row.id)">{{ row.approvalStatus }}</button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        }

        @if (activeTab() === 'history') {
          <section class="history">
            @for (item of relatedRates(); track item.id) {
              <article class="history-item">
                <h4>{{ item.fromCurrencyCode }} → {{ item.toCurrencyCode }} @ {{ item.rate }}</h4>
                <p>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}: {{ item.effectiveDateUtc | ainDate }}</p>
                <p>{{ t('columns.status') }}: {{ item.approvalStatus }}</p>
              </article>
            }
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 10px; }
    .tabs button { border: 1px solid #cbd5e1; border-radius: 999px; background: white; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 700; }
    .tabs button:disabled { opacity: .45; cursor: not-allowed; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .radio-wrapper { display: grid; gap: 6px; }
    .radio-group { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; min-height: 2.5rem; }
    .radio-item { display: inline-flex; align-items: center; gap: 8px; }
    .history { display: grid; gap: 8px; margin-top: 10px; }
    .history-item { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; }
    .history-item h4 { margin: 0 0 6px; font-size: 14px; }
    .history-item p { margin: 0; color: #475569; font-size: 12px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class FinanceControlCenterCurrencyDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly activeTab = signal<CurrencyDetailTab>('general');
  readonly isCreateMode = signal(false);
  readonly relatedRates = signal<FxRateSnapshotRecord[]>([]);
  readonly currentCurrency = signal<CurrencyDefinitionRecord | null>(null);

  readonly form = {
    code: '',
    name: '',
    symbol: '',
    decimalPlaces: 2,
    isActive: true
  };

  ngOnInit(): void {
    const code = this.route.snapshot.paramMap.get('code');
    if (!code) {
      this.error.set(this.t('finance.multiCurrency.errors.notFound'));
      this.loading.set(false);
      return;
    }

    if (code === 'new') {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    this.loadCurrency(code);
  }

  save(): void {
    this.error.set('');
    if (!this.form.code.trim() || !this.form.name.trim()) {
      this.error.set(this.t('finance.multiCurrency.errors.requiredFields'));
      return;
    }

    if (this.isCreateMode()) {
      this.api.createCurrency({
        code: this.form.code.trim(),
        name: this.form.name.trim(),
        symbol: this.form.symbol.trim(),
        decimalPlaces: Number(this.form.decimalPlaces ?? 0),
        isActive: this.form.isActive
      }).subscribe({
        next: created => {
          void this.router.navigate(['/financial/control-center/currencies', created.code], { queryParams: this.listQueryParams() });
        },
        error: () => this.error.set(this.t('common.saveFailed'))
      });
      return;
    }

    this.api.updateCurrency(this.form.code, {
      name: this.form.name.trim(),
      symbol: this.form.symbol.trim(),
      decimalPlaces: Number(this.form.decimalPlaces ?? 0),
      isActive: this.form.isActive
    }).subscribe({
      next: updated => {
        this.currentCurrency.set(updated);
        this.syncForm(updated);
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  createRateFromCurrency(): void {
    void this.router.navigate(['/financial/control-center/fx-rates', 'new'], {
      queryParams: {
        fromCurrencyCode: this.form.code,
        ...this.listQueryParams()
      }
    });
  }

  openRate(rateId: string): void {
    void this.router.navigate(['/financial/control-center/fx-rates', rateId], { queryParams: this.listQueryParams() });
  }

  private loadCurrency(code: string): void {
    this.loading.set(true);
    this.error.set('');
    this.api.getCurrency(code).subscribe({
      next: currency => {
        this.currentCurrency.set(currency);
        this.syncForm(currency);
        this.loadRelatedRates(currency.code);
      },
      error: () => {
        this.error.set(this.t('finance.multiCurrency.errors.notFound'));
        this.loading.set(false);
      }
    });
  }

  private loadRelatedRates(currencyCode: string): void {
    this.api.listFxRates({ fromCurrencyCode: currencyCode }).subscribe({
      next: fromRates => {
        this.api.listFxRates({ toCurrencyCode: currencyCode }).subscribe({
          next: toRates => {
            const merged = [...fromRates, ...toRates];
            const map = new Map(merged.map(item => [item.id, item]));
            const rows = Array.from(map.values()).sort((a, b) => b.effectiveDateUtc.localeCompare(a.effectiveDateUtc));
            this.relatedRates.set(rows);
            this.loading.set(false);
          },
          error: () => {
            this.relatedRates.set([]);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.relatedRates.set([]);
        this.loading.set(false);
      }
    });
  }

  private syncForm(currency: CurrencyDefinitionRecord): void {
    this.form.code = currency.code;
    this.form.name = currency.name;
    this.form.symbol = currency.symbol;
    this.form.decimalPlaces = currency.decimalPlaces;
    this.form.isActive = currency.isActive;
  }

  listQueryParams(): Record<string, string> {
    return {
      code: this.route.snapshot.queryParamMap.get('code') ?? '',
      name: this.route.snapshot.queryParamMap.get('name') ?? '',
      status: this.route.snapshot.queryParamMap.get('status') ?? '',
      showFilters: this.route.snapshot.queryParamMap.get('showFilters') ?? '1'
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
